import { apiPOST, REQUEST_METHOD } from 'utils/apiClient'
import { APIRoute } from 'routes/api'
import { getClubCourses } from 'store/club/thunks'
import { createApiThunk } from 'utils/thunkWrapper'
import { createAsyncThunk } from '@reduxjs/toolkit'
import { getMimeType } from 'utils/mimeType'
import download from 'downloadjs'
import { sendErrorNotification, sendSuccessNotification } from 'utils/notifications'
import { COURSE_DATA_SOURCE } from 'config'

export const getCourse = createApiThunk<Course, number>({
  action: 'course/get',
  method: REQUEST_METHOD.GET,
  formatUrl: (id) => APIRoute.GET_COURSE(id),
})

export const editCourse = createApiThunk<Course, CoursePayload>({
  action: 'course/edit',
  method: REQUEST_METHOD.PUT,
  formatUrl: (payload) => APIRoute.PUT_COURSE(payload.id),
  getData: (payload) => payload.data,
})

export const createCourse = createApiThunk<Course, CourseDataPayload>({
  action: 'course/create',
  method: REQUEST_METHOD.POST,
  formatUrl: () => APIRoute.POST_ADD_COURSE,
  getData: (payload) => payload,
  onSuccess: (payload, response, thunkApi) => {
    const course = response
    thunkApi.dispatch(generateInitialMapping({ courseId: course.id }))
  },
})

export const duplicateCourse = createApiThunk<Course, number>({
  action: 'course/duplicate',
  method: REQUEST_METHOD.POST,
  formatUrl: (id) => APIRoute.POST_DUPLICATE_COURSE(id),
})

export const createCourseVersion = createApiThunk<CourseVersion, CourseVersionPayload>({
  action: 'course/newVersion',
  method: REQUEST_METHOD.POST,
  formatUrl: (payload) => APIRoute.POST_COURSE_VERSION(payload.courseId),
  getData: (payload) => payload.data,
  getSuccessMessage: (payload) => payload.successMessage,
  onSuccess: (payload, response, thunkApi) => {
    const courseVersion = response
    if (payload.data.source === COURSE_DATA_SOURCE.GGB_USER) {
      thunkApi.dispatch(
        createCourseVersionZendeskTicket({
          courseId: payload.courseId,
          version: courseVersion.id,
          retryOnFailure: true,
        }),
      )
    }
  },
})

export const deactivateCourse = createApiThunk<Course, number>({
  action: 'course/deactivate',
  method: REQUEST_METHOD.PUT,
  formatUrl: (id) => APIRoute.PUT_COURSE_DEACTIVATE(id),
})

export const reactivateCourse = createApiThunk<Course, number>({
  action: 'course/reactivate',
  method: REQUEST_METHOD.PUT,
  formatUrl: (id) => APIRoute.PUT_COURSE_REACTIVATE(id),
})

export const setCourseVersionOfficial = createApiThunk<CourseVersion, number>({
  action: 'course/versionOfficial',
  method: REQUEST_METHOD.PUT,
  formatUrl: (id) => APIRoute.PUT_COURSE_VERSION_OFFICIAL(id),
})

export const setCourseVersionUnofficial = createApiThunk<CourseVersion, number>({
  action: 'course/versionUnofficial',
  method: REQUEST_METHOD.PUT,
  formatUrl: (id) => APIRoute.PUT_COURSE_VERSION_UNOFFICIAL(id),
})

export const setCourseVersionReviewed = createApiThunk<Course, CourseVersionReviewedPayload>({
  action: 'course/versionReviewed',
  method: REQUEST_METHOD.PUT,
  formatUrl: (payload) => APIRoute.PUT_COURSE_VERSION_REVIEWED(payload.id),
  getData: (payload) => payload.data,
})

export const transferCourse = createApiThunk<Course, TransferCoursePayload>({
  action: 'course/transfer',
  method: REQUEST_METHOD.POST,
  formatUrl: (payload) => APIRoute.POST_TRANSFER_COURSE(payload.courseId),
  getData: (payload) => payload.data,
  onSuccess: (payload, response, thunkApi) => {
    thunkApi.dispatch(getClubCourses(payload.data.clubId))
  },
})

export const generateInitialMapping = createApiThunk<Course, CourseInitialProviderMappingPayload>({
  action: 'course/generateInitialMapping',
  method: REQUEST_METHOD.POST,
  formatUrl: (payload) => APIRoute.POST_COURSE_GENERATE_INITIAL_MAPPING(payload.courseId),
  getData: (payload) => ({
    versionActiveStartTime: payload.data?.versionActiveStartTime,
  }),
})

export const transferCourseProviderMapping = createApiThunk<
  CourseProviderMappingTransferReturn,
  CourseProviderMappingTransferPayload
>({
  action: 'course/transferProviderMapping',
  method: REQUEST_METHOD.POST,
  formatUrl: () => APIRoute.POST_TRANSFER_COURSE_PROVIDER_MAPPING,
  getData: (payload) => payload.data,
  onSuccess: (payload, response, thunkApi) => {
    const officialVersions = response.fromCourse.versions?.filter((cv: CourseVersion) => cv.isOfficial)
    const originalActiveStartTime = officialVersions ? officialVersions[0]?.activeStartTime?.date : null

    thunkApi.dispatch(
      generateInitialMapping({
        courseId: response.fromCourse.id,
        data: { versionActiveStartTime: originalActiveStartTime },
      }),
    )
  },
})

export const doCourseTeeBoxMapping = createApiThunk<Course, CourseTeeBoxMappingPayload>({
  action: 'course/teeboxMapping',
  method: REQUEST_METHOD.POST,
  formatUrl: (payload) => APIRoute.POST_COURSE_TEEBOX_MAPPING(payload.courseId),
  getData: (payload) => payload.data,
})

export const resetCourseMapping = createApiThunk<Course, number>({
  action: 'course/resetMapping',
  method: REQUEST_METHOD.POST,
  formatUrl: (id) => APIRoute.POST_COURSE_RESET_MAPPING(id),
  onSuccess: (id, response, thunkApi) => {
    thunkApi.dispatch(generateInitialMapping({ courseId: id }))
  },
})

export const setCourseGpsVersionOfficial = createApiThunk<Course, CourseGpsVersionPayload>({
  action: 'course/gpsVersionOfficial',
  method: REQUEST_METHOD.PUT,
  formatUrl: (payload) => APIRoute.PUT_COURSE_GPS_VERSION_OFFICIAL(payload.courseId),
  getData: (payload) => payload.data,
})

export const setCourseGpsVersionUnofficial = createApiThunk<Course, CourseGpsVersionPayload>({
  action: 'course/gpsVersionUnofficial',
  method: REQUEST_METHOD.PUT,
  formatUrl: (payload) => APIRoute.PUT_COURSE_GPS_VERSION_UNOFFICIAL(payload.courseId),
  getData: (payload) => payload.data,
})

export const setCourseGpsVersionReviewed = createApiThunk<Course, CourseGpsVersionReviewedPayload>({
  action: 'course/gpsVersionReviewed',
  method: REQUEST_METHOD.PUT,
  formatUrl: (payload) => APIRoute.PUT_COURSE_GPS_VERSION_REVIEWED(payload.courseId, payload.gpsVersion),
  getData: (payload) => payload.data,
})

export const createCourseGpsVersion = createApiThunk<Course, CourseGpsVersionSavePayload>({
  action: 'course/createGpsVersion',
  method: REQUEST_METHOD.POST,
  formatUrl: (payload) => APIRoute.POST_COURSE_GPS_VERSION(payload.courseId),
  getData: (payload) => payload.data,
  onSuccess: (payload, response, thunkApi) => {
    const course = response
    if (payload.isUserEditor) {
      const gpsPoints = course.gpsPoints || []
      const latestGpsVersion = gpsPoints.sort((a, b) => b.gpsVersion - a.gpsVersion)[0].gpsVersion
      thunkApi.dispatch(
        createCourseGpsVersionZendeskTicket({
          courseId: payload.courseId,
          version: latestGpsVersion,
          retryOnFailure: true,
        }),
      )
    }
  },
})

export const createCourseGpsVersionZendeskTicket = createApiThunk<void, CourseGpsVersionZendeskPayload>({
  action: 'course/createGpsVersionZendeskTicket',
  method: REQUEST_METHOD.POST,
  formatUrl: (payload) => APIRoute.POST_COURSE_GPS_VERSION_ZENDESK(payload.courseId, payload.version),
  onFailure: (payload, thunkApi) => {
    if (payload.retryOnFailure) {
      thunkApi.dispatch(
        createCourseGpsVersionZendeskTicket({
          courseId: payload.courseId,
          version: payload.version,
          retryOnFailure: false,
        }),
      )
    }
  },
})

export const createCourseVersionZendeskTicket = createApiThunk<void, CourseVersionZendeskPayload>({
  action: 'course/createVersionZendeskTicket',
  method: REQUEST_METHOD.POST,
  formatUrl: (payload) => APIRoute.POST_COURSE_VERSION_ZENDESK(payload.courseId, payload.version),
  onFailure: (payload, thunkApi) => {
    if (payload.retryOnFailure) {
      thunkApi.dispatch(
        createCourseVersionZendeskTicket({
          courseId: payload.courseId,
          version: payload.version,
          retryOnFailure: false,
        }),
      )
    }
  },
})

export const flipCourseGpsVersionMapping = createApiThunk<Course, CourseGpsVersionPayload>({
  action: 'course/flipGsVersionMapping',
  method: REQUEST_METHOD.PUT,
  formatUrl: (payload) => APIRoute.PUT_COURSE_GPS_VERSION_FLIP_MAPPING(payload.courseId),
  getData: (payload) => payload.data,
})

export const copyCourseGpsMapping = createApiThunk<Course, CourseGpsMappingCopyPayload>({
  action: 'course/copyCourseGpsMapping',
  method: REQUEST_METHOD.PUT,
  formatUrl: (payload) => APIRoute.PUT_COURSE_GPS_COPY_MAPPING(payload.courseId),
  getData: (payload) => payload.data,
})

export const editCourseVersion = createApiThunk<Course, CourseVersionEditPayload>({
  action: 'course/versionEdit',
  method: REQUEST_METHOD.PUT,
  formatUrl: (payload) => APIRoute.PUT_COURSE_VERSION(payload.courseVersionId),
  getData: (payload) => payload.data,
})

export const initCourseHoleStats = createApiThunk<InitCourseHoleStatsResponse, InitCourseHoleStatsPayload>({
  action: 'course/hole-stats/init',
  method: REQUEST_METHOD.POST,
  formatUrl: (payload) => APIRoute.POST_COURSE_HOLE_STATS_INIT(payload.courseId),
  getData: (payload) => payload,
})

export const checkCourseHoleStatsStatus = createApiThunk<CourseHoleStatsStatusResponse, CourseHoleStatsStatusPayload>({
  action: 'course/hole-stats/status',
  method: REQUEST_METHOD.GET,
  formatUrl: (payload) => APIRoute.GET_COURSE_HOLE_STATS_STATUS(payload.courseId, payload.taskId),
  getData: (payload) => payload,
})

export const cancelCourseHoleStats = createApiThunk<void, CourseHoleStatsStatusPayload>({
  action: 'course/hole-stats/cancel',
  method: REQUEST_METHOD.DELETE,
  formatUrl: (payload) => APIRoute.DELETE_COURSE_HOLE_STATS(payload.courseId, payload.taskId),
})

export const getCourseHoleStatsResults = createAsyncThunk<any, any, { rejectValue: APIError }>(
  'course/hole-stats/results',
  async (payload: CourseHoleStatsStatusPayload, thunkApi) => {
    try {
      const response = await apiPOST({
        url: APIRoute.POST_COURSE_HOLE_STATS_RESULTS(payload.courseId, payload.taskId),
        contentType: getMimeType('xlsx'),
      })
      return download(response.data, 'Hole stat results', getMimeType('xlsx'))
    } catch (error: any) {
      return thunkApi.rejectWithValue(error.response.data.error)
    }
  },
)

export const fetchCourseById = createApiThunk<Course, number>({
  action: 'course/fetchById',
  method: REQUEST_METHOD.GET,
  formatUrl: (id) => APIRoute.GET_COURSE(id),
})

export const updateCourseProviderVersion = createApiThunk<
  CourseProviderVersionUpdateResponse,
  CourseProviderVersionUpdatePayload
>({
  action: 'course/updateCourseProviderVersion',
  method: REQUEST_METHOD.POST,
  formatUrl: (payload) => APIRoute.POST_UPDATE_COURSE_PROVIDER_VERSION(payload.providerId),
  getData: (payload) => payload,
  onSuccess: (payload, response, thunkApi) => {
    sendSuccessNotification('Provider course version updated successfully')
    thunkApi.dispatch(getCourse(payload.id))
  },
  onFailure: (payload, thunkApi, error) => {
    /**
     * TODO better error message management. Need refactoring on OFF backend side.
     * Error code and subcode will determine error message in the future.
     */
    const message = String(error.response.data.detail) || 'Something went wrong'
    sendErrorNotification(message.split('|')[1]?.trim() ?? message)
  },
})

export const getProviderCourses = createApiThunk<ProviderClubCourseResponse, ProviderCourseSearchPayload>({
  action: 'course/getProviderCourses',
  method: REQUEST_METHOD.GET,
  formatUrl: (payload) => APIRoute.GET_PROVIDER_CLUBS_AND_COURSES_SEARCH(payload.providerId),
  getParams: (payload) => ({
    searchString: payload.searchString,
  }),
  onSuccess(payload, response) {
    return { clubs: response.data }
  },
})

export const updateCourseProviderMapping = createApiThunk<UpdateProviderMappingResponse, UpdateProviderMappingPayload>({
  action: 'course/updateCourseProviderMapping',
  method: REQUEST_METHOD.PUT,
  formatUrl: (payload) => APIRoute.PUT_ADD_COURSE_PROVIDER_MAPPING(payload.courseId, payload.courseProviderMapId),
  getData: (payload) => payload,
  onSuccess: () => {
    sendSuccessNotification('Course Mapped succesfully')
  },
})

export const updateCourseProviderVersionMap = createApiThunk<
  CourseProviderVersionMapUpdateResponse,
  CourseProviderVersionMapUpdatePayload
>({
  action: 'course/forceUpdateCourseProviderVersion',
  method: REQUEST_METHOD.PUT,
  formatUrl: (payload) =>
    APIRoute.PUT_UPDATE_COURSE_PROVIDER_VERSION_MAP(
      payload.courseId,
      payload.courseVersionId,
      payload.courseProviderVersionId,
    ),
  getData: (payload) => payload,
  onSuccess: (payload, response, thunkApi) => {
    thunkApi.dispatch(
      updateCourseProviderVersion({
        providerId: response.providerUpdate.provider.id,
        courseVersionId: response.version,
        courseId: response.providerUpdate.providerCourseMap.providerCourseId,
        action: 'update',
        jurisdiction: payload.providerAbbreviation,
        id: payload.courseId,
      }),
    )
  },
})
